import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AutoAwesome as AiIcon } from '@mui/icons-material';
import { NodeViewWrapper, ReactNodeViewProps } from '@tiptap/react';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';

interface ChartSpec {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  labels: string[];
  data: number[];
}

/**
 * Renders an AI-generated chart/diagram block within the canvas (spec 044 US4/FR-018), using
 * the minimal chart-spec JSON shape AiCanvasActionService's multimodal decision step produces.
 * Falls back to a plain caption if the spec is missing or unparseable, rather than crashing
 * the whole canvas render.
 */
const ChartBlockNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { chartSpec, origin } = node.attrs as { chartSpec: string; origin: 'AI' | 'STUDENT' };
  const isAi = origin === 'AI';

  let spec: ChartSpec | null = null;
  if (chartSpec) {
    try {
      spec = JSON.parse(chartSpec);
    } catch {
      spec = null;
    }
  }

  return (
    <NodeViewWrapper as="div">
      <Box
        sx={{
          my: 1,
          p: 1.5,
          borderRadius: 1,
          bgcolor: isAi ? 'action.hover' : 'transparent',
          borderLeft: '3px solid',
          borderColor: isAi ? 'primary.main' : 'divider',
        }}
        data-origin={origin}
      >
        {isAi && (
          <Chip
            icon={<AiIcon sx={{ fontSize: '14px !important' }} />}
            label="AI"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', mb: 0.75 }}
          />
        )}
        {!spec ? (
          <Typography variant="caption" color="text.secondary">[Chart generating…]</Typography>
        ) : (
          <Box>
            {spec.title && <Typography variant="subtitle2" gutterBottom>{spec.title}</Typography>}
            {renderChart(spec)}
          </Box>
        )}
      </Box>
    </NodeViewWrapper>
  );
};

function renderChart(spec: ChartSpec) {
  const height = 240;
  switch (spec.type) {
    case 'pie':
      return (
        <PieChart
          series={[{ data: spec.labels.map((label, i) => ({ id: i, value: spec.data[i] ?? 0, label })) }]}
          height={height}
        />
      );
    case 'line':
      return (
        <LineChart
          xAxis={[{ scaleType: 'point', data: spec.labels }]}
          series={[{ data: spec.data }]}
          height={height}
        />
      );
    case 'bar':
    default:
      return (
        <BarChart
          xAxis={[{ scaleType: 'band', data: spec.labels }]}
          series={[{ data: spec.data }]}
          height={height}
        />
      );
  }
}

export default ChartBlockNode;
